import { useState } from 'react';
import { Add, SwapVert } from '@mui/icons-material';
import { Button, Grid, Stack } from '@mui/material';
import { InternalResourceDialog, ReorderResourcesDialog } from '..';
import { ExternalResourceDialog } from '@/components/partial/external-resource-dialog';
import type { ResourceGroup } from '@/data/learning-path/interfaces';
import type { Course } from '@/models/catalog/course';
import type { ExternalResource } from '@/models/resources';
import { ResourceItem } from '@/packages/learning-path/components/resource-item';
import { DeleteItemDialog } from '@/packages/learning-path/pages/path-management/components/delete-item-dialog';
import { useManagePath } from '@/packages/learning-path/pages/path-management/pages/edit-path/context';
import { getResourceId } from '@/packages/learning-path/pages/path-management/pages/edit-path/utils/get-resource-id';
import { mapCatalogCourseToEmbeddedCourse } from '@/utils/map-catalog-course-to-embedded-course';

export const ResourceBlock = ({ blockId }: { blockId: string }) => {
  const [showInternalResourceDialog, setShowInternalResourceDialog] =
    useState(false);
  const [showExternalResourceDialog, setShowExternalResourceDialog] =
    useState(false);
  const [editingResource, setEditingResource] = useState<ResourceGroup | null>(
    null
  );
  const [deleteTarget, setDeleteTarget] = useState<{
    blockId: string;
    resourceId?: string;
  } | null>(null);
  const [showReorderResourcesDialog, setShowReorderResourcesDialog] =
    useState(false);

  const {
    contents,
    removeBlock,
    removeResourceFromBlock,
    addResourceToBlock,
    updateResourceInBlock,
  } = useManagePath();

  const block = contents.find((block) => block.id === blockId);
  const resources =
    (block?.type === 'resource_group' && block?.content?.resource_groups) || [];

  const handleCloseDialogs = () => {
    setShowInternalResourceDialog(false);
    setShowExternalResourceDialog(false);
    setEditingResource(null);
  };

  const handleEdit = (resource: ResourceGroup) => {
    setEditingResource(resource);
    if ('external_link' in resource) {
      setShowExternalResourceDialog(true);
    } else if ('course' in resource) {
      setShowInternalResourceDialog(true);
    }
  };

  const handleSubmitExternal = (data: ExternalResource) => {
    const resource: ResourceGroup = {
      external_link: {
        ...data,
        description: data.name || '',
        id: editingResource
          ? getResourceId(editingResource)
          : crypto.randomUUID(),
      },
    };

    if (editingResource) {
      updateResourceInBlock(blockId, resource);
    } else {
      addResourceToBlock(blockId, resource);
    }

    handleCloseDialogs();
  };

  const handleSubmitInternal = (data: Course) => {
    const resource: ResourceGroup = {
      course: {
        ...mapCatalogCourseToEmbeddedCourse(data),
        id: editingResource
          ? getResourceId(editingResource) || ''
          : crypto.randomUUID(),
      },
    };
    addResourceToBlock(blockId, resource);
    handleCloseDialogs();
  };

  const handleDeleteItem = () => {
    if (deleteTarget?.resourceId) {
      removeResourceFromBlock(deleteTarget?.blockId, deleteTarget?.resourceId);
    } else {
      removeBlock(deleteTarget?.blockId || '');
    }
    setDeleteTarget(null);
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        columns={{ xs: 1, sm: 2, md: 4 }}
        sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}
      >
        {resources?.map((resource: ResourceGroup) => {
          return (
            <Grid size={1} key={getResourceId(resource)}>
              <ResourceItem
                key={getResourceId(resource)}
                resource={resource}
                onDelete={() =>
                  setDeleteTarget({
                    blockId,
                    resourceId: getResourceId(resource),
                  })
                }
                onEdit={() => handleEdit(resource)}
              />
            </Grid>
          );
        })}
        <Stack spacing={2} width={{ xs: '100%', md: 267 }}>
          <Button
            sx={{ height: 94, backgroundColor: 'neutral.0' }}
            startIcon={<Add />}
            onClick={() => {
              setEditingResource(null);
              setShowInternalResourceDialog(true);
            }}
          >
            Adicionar recurso na Galena
          </Button>
          <Button
            sx={{ height: 94, backgroundColor: 'neutral.0' }}
            startIcon={<Add />}
            onClick={() => {
              setEditingResource(null);
              setShowExternalResourceDialog(true);
            }}
          >
            Adicionar recurso externo
          </Button>
          <Button
            sx={{ height: 94, backgroundColor: 'neutral.0' }}
            startIcon={<SwapVert />}
            disabled={resources?.length <= 1}
            onClick={() => setShowReorderResourcesDialog(true)}
          >
            Reordenar recursos
          </Button>
        </Stack>
        {showInternalResourceDialog && (
          <InternalResourceDialog
            onClose={handleCloseDialogs}
            onSubmit={handleSubmitInternal}
          />
        )}
        {showExternalResourceDialog && (
          <ExternalResourceDialog
            onClose={handleCloseDialogs}
            defaultValues={editingResource?.external_link}
            onSubmit={handleSubmitExternal}
            showCoverImage
          />
        )}
      </Grid>
      <Stack direction="row" justifyContent="flex-end">
        <Button color="error" onClick={() => setDeleteTarget({ blockId })}>
          Excluir seção
        </Button>
      </Stack>
      {deleteTarget && (
        <DeleteItemDialog
          onClose={() => setDeleteTarget(null)}
          onSubmit={handleDeleteItem}
        />
      )}
      {showReorderResourcesDialog && (
        <ReorderResourcesDialog
          onClose={() => setShowReorderResourcesDialog(false)}
          blockId={blockId}
        />
      )}
    </>
  );
};
